import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { camControlOverlayPath, overlayPath, OverlayKind } from 'lib/paths'
import { onStreamKeyReset } from 'lib/streamKey'
import { OverlayLink } from 'types/api'
import listOverlayLinksQuery from 'graphql/listOverlayLinks.graphql'
import mintOverlayLinkMutation from 'graphql/mintOverlayLink.graphql'
import revokeOverlayLinkMutation from 'graphql/revokeOverlayLink.graphql'

export const DEFAULT_OVERLAY_LINK_LABEL = 'Primary'

const PRIMARY_LINK_CONNECTION_CAP = 20

export interface OverlayLinksState {
  links: OverlayLink[] | null
  busy: boolean
  error: string | null
  refresh: () => Promise<void>
  mint: (label: string, connectionCap?: number) => Promise<OverlayLink | null>
  revoke: (linkId: string) => Promise<void>
}

export const useOverlayLinks = (eventId: string): OverlayLinksState => {
  const { auth0Token } = useAuth()
  const [links, setLinks] = useState<OverlayLink[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setBusy(true)
    setError(null)
    try {
      const { links: rows } = await graphqlRequest<{ links: OverlayLink[] }>(
        listOverlayLinksQuery, { eventId }, auth0Token
      )
      setLinks(rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overlay links')
    } finally {
      setBusy(false)
    }
  }, [eventId, auth0Token])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const mint = useCallback(async (label: string, connectionCap?: number): Promise<OverlayLink | null> => {
    if (auth0Token === undefined) return null
    setBusy(true)
    setError(null)
    try {
      const { link } = await graphqlRequest<{ link: OverlayLink }>(
        mintOverlayLinkMutation,
        { eventId, label, ...(connectionCap !== undefined ? { connectionCap } : {}) },
        auth0Token
      )
      setLinks((prev) => prev === null ? [link] : [...prev, link])
      return link
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mint overlay link')
      return null
    } finally {
      setBusy(false)
    }
  }, [eventId, auth0Token])

  const revoke = useCallback(async (linkId: string): Promise<void> => {
    if (auth0Token === undefined) return
    setBusy(true)
    setError(null)
    try {
      const { link } = await graphqlRequest<{ link: OverlayLink }>(
        revokeOverlayLinkMutation, { eventId, linkId }, auth0Token
      )
      setLinks((prev) => prev === null
        ? prev
        : prev.map((l) => l.linkId === link.linkId ? link : l))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke overlay link')
    } finally {
      setBusy(false)
    }
  }, [eventId, auth0Token])

  return { links, busy, error, refresh, mint, revoke }
}

export interface DefaultOverlayLinkState {
  k: string | null
  busy: boolean
  error: string | null
  recreate: () => Promise<void>
}

export const useDefaultOverlayLink = (eventId: string): DefaultOverlayLinkState => {
  const { auth0Token } = useAuth()
  const [resolved, setResolved] = useState<{ eventId: string, k: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seqRef = useRef(0)

  const ensure = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    seqRef.current += 1
    const seq = seqRef.current
    setBusy(true)
    setError(null)
    try {
      const { link } = await graphqlRequest<{ link: OverlayLink }>(
        mintOverlayLinkMutation,
        { eventId, label: DEFAULT_OVERLAY_LINK_LABEL, connectionCap: PRIMARY_LINK_CONNECTION_CAP },
        auth0Token
      )
      if (seq !== seqRef.current) return
      if (typeof link.k !== 'string' || link.k === '') {
        setError('Could not load this stream\'s key. Reload the page to try again.')
        return
      }
      setResolved({ eventId, k: link.k })
    } catch (err) {
      if (seq !== seqRef.current) return
      setError(err instanceof Error ? err.message : 'Could not load this stream\'s key.')
    } finally {
      if (seq === seqRef.current) setBusy(false)
    }
  }, [eventId, auth0Token])

  useEffect(() => {
    void ensure()
  }, [ensure])

  const recreate = useCallback(async (): Promise<void> => {
    setResolved(null)
    await ensure()
  }, [ensure])

  useEffect(() => onStreamKeyReset(eventId, () => { void recreate() }), [eventId, recreate])

  const k = resolved !== null && resolved.eventId === eventId ? resolved.k : null

  return { k, busy, error, recreate }
}

export const buildOverlayShortUrl = (
  slug: string,
  kind: OverlayKind,
  k: string | null,
  origin: string = (typeof globalThis.location !== 'undefined' ? globalThis.location.origin : '')
): string | null => {
  if (k === null) return null
  return `${origin}${overlayPath(slug, kind)}?k=${k}`
}

export const buildCamControlShortUrl = (
  slug: string,
  camControlId: string,
  k: string | null,
  origin: string = (typeof globalThis.location !== 'undefined' ? globalThis.location.origin : '')
): string | null => {
  if (k === null) return null
  return `${origin}${camControlOverlayPath(slug, camControlId)}?k=${k}`
}
